/**
 * GoalsShowcase — the client Goals / Life Vision surface rendered from demo data
 * for the Coaching OS marketing surface. A faithful, prop-only replica of the
 * goal list of the time (the real `GoalRow` decrypts its text and types its prop
 * from `lib/server`, so it cannot be lifted), reusing the genuine edge-safe
 * leaves — `GoalStatusBadge`/`GoalTimelineBadge` + `ui/progress` — so the badges
 * and milestone bar match the product exactly.
 *
 * Signature interaction: a row of category pills filters the visible goal cards,
 * animated via `motion/react` layout transitions. Under reduced motion the
 * filter is instant. The cards stagger in on load via `hearthEntrance`.
 *
 * Edge-safe: no router/query/encryption providers, no real navigation. Renders
 * inside the shared `ShowcaseFrame` ("feature" variant) so it reads as a
 * contained product window — one of the two homepage feature visuals.
 *
 * Aliases: goals showcase, life vision showcase, life design showcase.
 */

import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, useMemo, useState } from "react";

import {
  GoalStatusBadge,
  GoalTimelineBadge,
} from "~/components/ui/goal-badges";
import { Progress } from "~/components/ui/progress";
import { cn } from "~/lib/cn";
import { useReducedMotionSafe } from "~/lib/hooks/use-reduced-motion-safe";
import { hearthEntrance } from "~/lib/shared/hearth-entrance";

import { SHOWCASE_GOALS, type ShowcaseGoal } from "./showcase-fixtures";
import { ShowcaseFrame } from "./showcase-frame";

/** The "show everything" sentinel — never collides with a real category. */
const ALL_CATEGORIES = "__all__";

/** One goal card in the grid — visual replica of the real `GoalCard` body. */
function ShowcaseGoalCard({
  goal,
  order,
  reduce,
}: {
  readonly goal: ShowcaseGoal;
  readonly order: number;
  readonly reduce: boolean;
}): ReactNode {
  const progress =
    goal.milestonesTotal > 0
      ? Math.round((goal.milestonesCompleted / goal.milestonesTotal) * 100)
      : 0;

  return (
    <motion.div
      layout={!reduce}
      {...hearthEntrance(order, reduce)}
      exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
      className="rounded-xl border border-gray-100 bg-white p-4 transition-shadow hover:shadow-sm"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <h4 className="text-base font-medium text-gray-900">{goal.title}</h4>
        <div className="flex shrink-0 items-center gap-2">
          <GoalTimelineBadge timeline={goal.timeline} />
          <GoalStatusBadge status={goal.status} />
        </div>
      </div>
      <div className="flex items-center gap-3 @max-sm:flex-col @max-sm:items-start @max-sm:gap-2">
        <Progress value={progress} className="h-2 bg-gray-100" />
        <span className="text-xs whitespace-nowrap text-gray-500">
          Completed {goal.milestonesCompleted} of {goal.milestonesTotal}{" "}
          milestones
        </span>
      </div>
    </motion.div>
  );
}

/** An interactive category-filter pill — purple tint mirrors GoalCategoryBadge. */
function CategoryPill({
  label,
  active,
  onClick,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-purple-600 text-white"
          : "bg-purple-50 text-purple-700 hover:bg-purple-100",
      )}
    >
      {label}
    </button>
  );
}

export function GoalsShowcase(): ReactNode {
  const reduce = useReducedMotionSafe();
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);

  // Only categories that actually have goals get a pill, so no pill ever
  // filters to an empty grid.
  const categories = useMemo(
    () => Array.from(new Set(SHOWCASE_GOALS.map((goal) => goal.category))),
    [],
  );

  const visibleGoals =
    activeCategory === ALL_CATEGORIES
      ? SHOWCASE_GOALS
      : SHOWCASE_GOALS.filter((goal) => goal.category === activeCategory);

  return (
    <ShowcaseFrame variant="feature">
      <div className="flex flex-col gap-5 bg-gray-50/60 p-4">
        <div>
          {/* h3, not h1: this replica renders inside a marketing section whose
              real heading is an h2 — the page's single h1 is the hero's. */}
          <h3 className="font-heading text-2xl font-medium text-gray-900">
            Life Vision
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            The goals you and your coach are moving on, together.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <CategoryPill
            label="All"
            active={activeCategory === ALL_CATEGORIES}
            onClick={() => setActiveCategory(ALL_CATEGORIES)}
          />
          {categories.map((category) => (
            <CategoryPill
              key={category}
              label={category}
              active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            />
          ))}
        </div>

        <motion.div layout={!reduce} className="flex flex-col gap-3">
          {reduce ? (
            // Reduced motion: render the visible set directly so filtered cards
            // unmount instantly (no layout/exit animation).
            visibleGoals.map((goal, index) => (
              <ShowcaseGoalCard
                key={goal.id}
                goal={goal}
                order={index}
                reduce={reduce}
              />
            ))
          ) : (
            <AnimatePresence initial={false} mode="popLayout">
              {visibleGoals.map((goal, index) => (
                <ShowcaseGoalCard
                  key={goal.id}
                  goal={goal}
                  order={index}
                  reduce={reduce}
                />
              ))}
            </AnimatePresence>
          )}
        </motion.div>
      </div>
    </ShowcaseFrame>
  );
}
